import { action, thunk } from 'easy-peasy';
import { getProjectDocument, fetchPPTJson, claimProjectAPI } from '../../helper/project.service';
import toast from 'react-hot-toast';

const projectsModel = {
  // State
  currentProject: null,
  currentProjectId: null,
  loading: false,
  error: null,
  assetsLoaded: false,
  viewMode: 'preview',
  isViewModeSwitched: false,
  version: null,
  projectLanguage: null,
  isTranslating: false,

  // Actions
  setCurrentProject: action((state, payload) => {
    state.currentProject = payload;
    state.version = payload?.version ?? 1;
  }),

  setCurrentProjectId: action((state, payload) => {
    state.currentProjectId = payload;
  }),

  setLoading: action((state, payload) => {
    state.loading = payload;
  }),

  setError: action((state, payload) => {
    state.error = payload;
  }),

  clearProject: action((state) => {
    state.currentProject = null;
    state.currentProjectId = null;
    state.error = null;
    state.loading = false;
    state.assetsLoaded = false;
    state.viewMode = 'preview';
    state.isViewModeSwitched = false;
    state.version = null;
    state.projectLanguage = null;
    state.isTranslating = false;
  }),

  setIsTranslating: action((state, payload) => {
    state.isTranslating = payload;
  }),

  setProjectLanguage: action((state, payload) => {
    state.projectLanguage = payload;
    if (state.currentProject) {
      state.currentProject.language = payload;
    }
  }),

  setAssetsLoaded: action((state, payload) => {
    state.assetsLoaded = payload;
  }),

  setProjectAvatarId: action((state, avatarId) => {
    if (state.currentProject) {
      state.currentProject.avatarId = avatarId ?? null;
    }
  }),

  updateViewMode: action((state, payload) => {
    state.viewMode = payload;
    state.isViewModeSwitched = true;
  }),

  setIsViewModeSwitched: action((state, payload) => {
    state.isViewModeSwitched = payload;
  }),

  // Thunks
  setViewMode: thunk((actions, payload, { getStoreActions, getState }) => {
    const currentState = getState();
    if (currentState.viewMode === payload) {
      return;
    }
    actions.updateViewMode(payload);
    const mode = payload === 'edit' ? 'edit' : 'preview';
    const { setIsPlaying } = getStoreActions().timeline;
    if (mode === 'edit') {
      setIsPlaying(false);
    }
    actions.setIsViewModeSwitched(true);
  }),

  loadProject: thunk(async (actions, projectId, { getStoreActions }) => {
    try {
      actions.setLoading(true);
      actions.setError(null);
      actions.setCurrentProjectId(projectId);
      actions.setViewMode('preview');
      actions.setIsViewModeSwitched(false);

      // Get project document
      const projectData = await getProjectDocument(projectId);
      actions.setCurrentProject(projectData);
      actions.setProjectLanguage(projectData.language);

      // If extraction done, fetch and set PPT JSON
      if (projectData.status === 'EXTRACTION_DONE' && projectData.json) {
        const pptJson = await fetchPPTJson(projectData.json);

        // Update ppt store
        const { setPPTJSON } = getStoreActions().ppt;
        setPPTJSON(pptJson);
      }

      actions.setLoading(false);
    } catch (error) {
      actions.setError(error.message);
      actions.setLoading(false);
      console.error('Load project error:', error);
    }
  }),
  // Claim project thunk
  handleProjectClaim: thunk(async (actions, { projectId, isNewUser = false }) => {
    try {
      actions.setLoading(true);
      actions.setError(null);
      toast.loading('Claiming your project...', { id: 'claim' });

      const result = await claimProjectAPI(projectId);
      // Handle SUCCESS
      if (result.status === 'SUCCESS') {
        toast.success('Project claimed successfully!', { id: 'claim' });
        localStorage.removeItem('guestProjectId');
        actions.setCurrentProjectId(projectId);
        if (isNewUser) {
          // Redirect new users to pricing page
          window.location.href = `/pricing/?projectId=${projectId}`;
        } else {
          window.location.href = `/processing/${projectId}`;
        }
        return { success: true };
      }

      // Handle ERROR cases
      if (result.status === 'ERROR') {
        switch (result.error) {
          case 'ALREADY_CLAIMED':
            toast('Project already claimed. Redirecting...', { id: 'claim' });
            localStorage.removeItem('guestProjectId');
            window.location.href = `/processing/${projectId}`;
            return { success: true };

          case 'UNAUTHORIZED':
            toast.error('You are not authorized to claim this project', { id: 'claim' });
            localStorage.removeItem('guestProjectId');
            actions.setError('UNAUTHORIZED');
            window.location.href = '/dashboard';
            return { success: false, error: 'UNAUTHORIZED' };

          case 'PROJECT_ID_REQUIRED':
            toast.error('Project ID is required', { id: 'claim' });
            localStorage.removeItem('guestProjectId');
            actions.setError('PROJECT_ID_REQUIRED');
            window.location.href = '/dashboard';
            return { success: false, error: 'PROJECT_ID_REQUIRED' };

          case 'INVALID_PROJECT_ID':
            toast.error('Invalid project ID', { id: 'claim' });
            localStorage.removeItem('guestProjectId');
            actions.setError('INVALID_PROJECT_ID');
            window.location.href = '/dashboard';
            return { success: false, error: 'INVALID_PROJECT_ID' };

          default:
            toast.error('Failed to claim project: ' + result.error, { id: 'claim' });
            localStorage.removeItem('guestProjectId');
            actions.setError(result.error);
            window.location.href = '/dashboard';
            return { success: false, error: result.error };
        }
      }

      // Unexpected response
      throw new Error('Unexpected response from server');
    } catch (error) {
      console.error('Claim project error:', error);
      toast.error('Failed to claim project. Please try again.', { id: 'claim' });
      localStorage.removeItem('guestProjectId');
      actions.setError(error.message);
      window.location.href = '/dashboard';
      return { success: false, error: error.message };
    } finally {
      actions.setLoading(false);
    }
  })
};

export default projectsModel;
